import { Col, Row } from 'antd';
import { Button } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonFilter from 'components/Button/ButtonFilter';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import path from 'routes/Path';
import { icons } from 'utils/constants/icons';
import FilterPaymentSummaryV2 from './FilterPaymentSummaryV2';
import TablePaymentSummaryV2 from './TablePaymentSummaryV2';
import { useMutation } from '@tanstack/react-query';
import { PaymentSummaryV2Apis } from 'apis/PaymentSummaryV2Apis';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { TEAM_TYPE } from 'interfaces/TeamSale/TeamSale';
import useQueryParam from 'hooks/useQueryPrams';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { IBillPaymentListData, IFilterValues, ITableV2Handle } from './types';

const PaymentSummaryPageV2 = () => {
  const { user } = useUser();
  const childRef = useRef<ITableV2Handle | null>(null);
  const { PlusOutlined } = icons;
  const navigate = useNavigate();
  const { setTitle, setBreadcrumb } = useTitle();

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const [data, setData] = useState<IBillPaymentListData>({
    items: [],
    pageInfo: {
      currentPage: 1,
      itemsPerPage: DEFAULT_PAGE_SIZE,
      totalItems: 0,
    },
  });
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [formParams, setFormParams] = useState<IFilterValues>({});
  const [exportLoading, setExportLoading] = useState(false);

  const defaultFilter = useMemo<IFilterValues>(
    () => ({
      plantId:
        user?.isAdmin || user?.isHO ? undefined : user?.plantCode || '5001',
      customerId: undefined,
      teamSaleId: user?.isSale ? user?.teamSales?.[0]?.id : undefined,
      fromDateToDate: [dayjs().subtract(1, 'month'), dayjs()],
    }),
    [user],
  );

  const mutateGetTHTT = useMutation({
    mutationFn: PaymentSummaryV2Apis.getMobileBillPaymentList,
    onSuccess: (res) => {
      const result = res?.data;
      if (result?.items) {
        setData({
          items: result.items || [],
          pageInfo: result.pageInfo || {
            currentPage: page,
            itemsPerPage: page_size,
            totalItems: (result.items || []).length,
          },
        });
      } else if (Array.isArray(result)) {
        setData({
          items: result,
          pageInfo: {
            currentPage: 1,
            itemsPerPage: 1000,
            totalItems: result.length,
          },
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutateRef = useRef(mutateGetTHTT);
  mutateRef.current = mutateGetTHTT;

  useEffect(() => {
    setTitle('Bảng Tổng hợp thanh toán');
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Bảng Tổng hợp thanh toán' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const refetchGetTHTT = useCallback(() => {
    const filters = { ...defaultFilter, ...formParams };

    const fromDateVal = filters.fromDateToDate?.[0]
      ? dayjs(filters.fromDateToDate[0]).startOf('day').toISOString()
      : undefined;
    const toDateVal = filters.fromDateToDate?.[1]
      ? dayjs(filters.fromDateToDate[1]).endOf('day').toISOString()
      : undefined;

    const effectivePlantId =
      user?.isAdmin || user?.isHO ? filters.plantId : user?.plantCode || '5001';

    mutateRef.current.mutate({
      page,
      limit: page_size,
      customerId: filters.customerId || 'all',
      teamSaleId: filters.teamSaleId || 'all',
      fromDate: fromDateVal,
      toDate: toDateVal,
      statusId: filters.statusId,
      plantId: effectivePlantId,
      id: filters.id,
      createId: user?.isAdmin || user?.isAccountant ? undefined : user?.id,
    });
  }, [formParams, defaultFilter, page, page_size, user]);

  useEffect(() => {
    refetchGetTHTT();
  }, [refetchGetTHTT]);

  const handleExportExcel = () => {
    setExportLoading(true);
    if (childRef.current) {
      childRef.current.exportExcel();
    }
    setTimeout(() => {
      setExportLoading(false);
    }, 1000);
  };

  const handleOkFilter = useCallback((values: IFilterValues) => {
    setFormParams(values);
  }, []);

  const handleCancelFilter = useCallback(() => {
    setFormParams({});
  }, []);

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport
            onClick={handleExportExcel}
            loading={exportLoading}
            disabled={!data?.pageInfo?.totalItems}
          />
        </Col>
        {(user?.isAdmin || user?.isAccountant) && (
          <Col>
            <Button
              fill
              title="Tạo mới"
              iconBefore={<PlusOutlined />}
              handleOnclick={() =>
                navigate(path.PAYMENT_SUMMARY_DETAIL_V2, {
                  state: {
                    isAdd: true,
                    teamTypeId: TEAM_TYPE.stay,
                  },
                })
              }
            />
          </Col>
        )}
      </Row>

      <FilterPaymentSummaryV2
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onFinish={handleOkFilter}
        onCancel={handleCancelFilter}
      />
      <TablePaymentSummaryV2
        ref={childRef}
        data={data}
        loading={mutateGetTHTT.isPending}
        refetch={refetchGetTHTT}
      />
    </>
  );
};

export default PaymentSummaryPageV2;
